import { Injectable } from '@angular/core';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { AcqnDashboardDetails } from '../model/AcquisitionDetails';

@Injectable({
  providedIn: 'root'
})
export class AcqnDownloadService {

  private confidential_info = 'Fannie Mae Confidential and Proprietary'; // Constant for confidential information
  private confidential_info_body = '** This data is for informational purposes only. The information represents only the data compiled for those Mortgage Loans and associated properties delivered during the specified date range and the other applied filters. The list may be incomplete, and the data may contain inaccuracies based on incorrect input or incorrect association to the Mortgage Loan. The presence or absence of a Mortgage Loan and associated data on the list does not establish compliance with the requirements and you should check your systems and submissions to ensure that complete data was submitted as and when required.';

  constructor() { 
  }

  public exportToExcel(dataRaw: AcqnDashboardDetails[], filters: any): void {

    // Create a new workbook and worksheets
    const workbook = new ExcelJS.Workbook();
    this.createIntroduction(workbook, filters);
    this.createComplianceReport(workbook, dataRaw);

    // Export the workbook as a blob
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, this.generateFileName('Digitizer Compliance Reporting', 'xlsx'));
    });
  }

  private createIntroduction(workbook: any, filters: any) {
    const worksheetIntroduction = workbook.addWorksheet('Report Inputs');
    worksheetIntroduction.getCell('A1').value = 'DUS Digitizer Data Submission Compliance Report';
    // Bold the text, increase font size, and set background color for cell A1
    worksheetIntroduction.getCell('A1').font = {
      bold: true,
      size: 16 // Large font size
    };
    worksheetIntroduction.getCell('A1').fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'D9D9D9' } // // Light grey background
    };
    // Set the width of column A
    worksheetIntroduction.getColumn('A').width = 80; // Adjust the width as needed
    // Set the height of row 1
    worksheetIntroduction.getRow(1).height = 30; // Adjust the height as needed
    // Vertically center the content in row 1
    worksheetIntroduction.getCell('A1').alignment = {
      vertical: 'middle'
    };

    // Add filters to the introduction worksheet
    worksheetIntroduction.addRow(['Filtered by:']);
    let dateRange = '  Date Range: ' + filters.startDate + ' - ' + filters.endDate;
    worksheetIntroduction.addRow([dateRange]);
 
    if (filters.selectedLender) {
      worksheetIntroduction.addRow(['  Lender: ' + filters.selectedLender]);
    } else {
      worksheetIntroduction.addRow(['  Lender: All']);
    }

    if (filters.acquisitionStatus) {
      worksheetIntroduction.addRow(['  Acquisition Status: ' + filters.acquisitionStatus]);
    } else {
      worksheetIntroduction.addRow(['  Acquisition Status: All']);
    }

    if (filters.supplemental) {
      worksheetIntroduction.addRow(['  Supplemental: ' + filters.supplemental]);
    } else {
      worksheetIntroduction.addRow(['  Supplemental: All']);
    }

    if (filters.loadFinanceType) {
      worksheetIntroduction.addRow(['  Loan Refinance Type: ' + filters.loadFinanceType]);
    } else {
      worksheetIntroduction.addRow(['  Loan Refinance Type: All']);
    }

    if (filters.smallLoanBalance) {
      worksheetIntroduction.addRow(['  Small Loan Balance: ' + filters.smallLoanBalance]);
    } else {
      worksheetIntroduction.addRow(['  Small Loan Balance: All']);
    }

    if (filters.transactionType) {
      worksheetIntroduction.addRow(['  Transaction Type: ' + filters.transactionType]);
    } else {
      worksheetIntroduction.addRow(['  Transaction Type: All']);
    }

    if (filters.tableSearchText) {
      worksheetIntroduction.addRow(['  Search Text: ' + filters.tableSearchText]);
    } else {
      worksheetIntroduction.addRow(['  Search Text: All']);
    }

    worksheetIntroduction.addRow([' ']); // Add an empty row for spacing
    worksheetIntroduction.addRow([' ']); // Add an empty row for spacing
    let row = worksheetIntroduction.addRow([this.confidential_info]);
    row.font = { bold: true };

    row = worksheetIntroduction.addRow([this.confidential_info_body]);
    row.getCell(1).alignment = { wrapText: true };
  }

  private createComplianceReport(workbook: any, dataRaw: AcqnDashboardDetails[]) {
    const worksheetComplianceReport = workbook.addWorksheet('Digitizer Compliance Report');

    // Define headers for the Excel sheet
    const headers = [
      'Seller Name',
      'Collateral Reference Number',
      'DUS Gateway Deal ID',
      'Property Name',
      'Loan Acquisition Date',
      'Submission Deadline',
      'Appraisal (APPR) Report Submission Date',
      'Days APPR Received Prior To / (After) Submission Deadline',
      'Environmental Site Assessment (ESA) Report Submission Date',
      'Days ESA Received Prior To / (After) Submission Deadline',
      'Property Condition Assessment (PCA) Report Submission Date',
      'Days PCA Received Prior To / (After) Submission Deadline',
      'Seismic Risk Assessment (SRA) Report Submission Date',
      'Days SRA Received Prior To / (After) Submission Deadline',
      'Zoning (ZON) Report Submission Date',
      'Days ZON Received Prior To / (After) Submission Deadline'
    ];

    // Prepare data rows from the input data
    const data: string[][] = [];
    dataRaw.forEach((item, row) => {
      row += 1; // Increment row counter
      console.log(
        'row = ' + row,
        "aqsnDt = " + this.convertDateToMMDDYYYY(row, item.aqsnDt, '') +
        'reqdSubmtDt = ' + this.convertDateToMMDDYYYY(row, item.reqdSubmtDt, '')
      );

      // Push formatted data into the array
      data.push([item.lender,
      item.coltRefNo,
      item.dusDealId,
      item.propNme,
      this.convertDateToMMDDYYYY(row, item.aqsnDt, ''),
      this.convertDateToMMDDYYYY(row, item.reqdSubmtDt, ''),
      this.convertDateToMMDDYYYY(row, item.fileRcvdDttmAPPR, item.appr),
      this.calculateDaysBetweenDates(row, item.fileRcvdDttmAPPR, item.reqdSubmtDt, item.appr),
      this.convertDateToMMDDYYYY(row, item.fileRcvdDttmESA, item.esa),
      this.calculateDaysBetweenDates(row, item.fileRcvdDttmESA, item.reqdSubmtDt, item.esa),
      this.convertDateToMMDDYYYY(row, item.fileRcvdDttmPCA, item.pca),
      this.calculateDaysBetweenDates(row, item.fileRcvdDttmPCA, item.reqdSubmtDt, item.pca),
      this.convertDateToMMDDYYYY(row, item.fileRcvdDttmSRA, item.sra),
      this.calculateDaysBetweenDates(row, item.fileRcvdDttmSRA, item.reqdSubmtDt, item.sra),
      this.convertDateToMMDDYYYY(row, item.fileRcvdDttmZON, item.zon),
      this.calculateDaysBetweenDates(row, item.fileRcvdDttmZON, item.reqdSubmtDt, item.zon),
      ]);
    });

    // Add headers to the worksheet
    worksheetComplianceReport.addRow(headers);

    // Style the header row
    const headerRow = worksheetComplianceReport.getRow(1);
    headerRow.eachCell((cell) => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } }; // Light grey background
      cell.font = { bold: false, color: { argb: '000000' } }; // Black text
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }; // Center alignment
      cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; // Thin border
    });

    // Add data rows to the worksheet
    data.forEach((row, rowIndex) => {
      const excelRow = worksheetComplianceReport.addRow(row);

      // Apply red font to specific cells with brackets
      row.forEach((cellValue, colIndex) => {
        if (colIndex > 6 && typeof cellValue === 'string' && /\(\d+\)/.test(cellValue)) {
          const cell = excelRow.getCell(colIndex + 1);
          cell.font = { color: { argb: 'FFFF0000' } }; // Red color
        }
      });
    });

    // Freeze the top row
    worksheetComplianceReport.views = [{ state: 'frozen', ySplit: 1 }];

    // Apply alternating column shading and alignment
    worksheetComplianceReport.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // Skip the header row
        row.eachCell((cell, colNumber) => {
          if (colNumber > 7 && colNumber % 2 === 0) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } }; // Light gray background
          }
          if (colNumber === 2 || colNumber >= 5) {
            cell.alignment = { horizontal: 'right' }; // Right-align specific columns
          }
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } }; // Thin border
        });
      }
    });

    // Set fixed column widths
    const columnWidth = 15;
    const columnWidthLarge = 55;
    worksheetComplianceReport.columns = [
      { width: columnWidthLarge }, // Seller Name
      { width: columnWidth + 1 }, // Collateral Reference #
      { width: columnWidth }, // DUS Gateway Deal ID
      { width: columnWidthLarge }, // Property Name
      { width: columnWidth }, // Loan Acquisition Date
      { width: columnWidth }, // Submission Deadline
      { width: columnWidth }, // Appraisal (APPR) Report Submission Date
      { width: columnWidth }, // Days APPR Received Prior To / (After) Submission Deadline
      { width: columnWidth }, // Environmental Site Assessment (ESA) Report Submission Date
      { width: columnWidth }, // Days ESA Received Prior To / (After) Submission Deadline
      { width: columnWidth }, // Property Condition Assessment (PCA) Report Submission Date
      { width: columnWidth }, // Days PCA Received Prior To / (After) Submission Deadline
      { width: columnWidth }, // Seismic Risk Assessment (SRA) Report Submission Date
      { width: columnWidth }, // Days SRA Received Prior To / (After) Submission Deadline
      { width: columnWidth }, // Zoning (ZON) Report Submission Date
      { width: columnWidth }
    ];

    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing
    worksheetComplianceReport.addRow([' ']); // Add an empty row for spacing

    let row = worksheetComplianceReport.addRow([this.confidential_info]);
    row.font = { bold: true };
    // Merge the next two cells in the same row
    let rowIndex = row.number; // Get the current row index
    worksheetComplianceReport.mergeCells(rowIndex, 1, rowIndex, 4); // Merge cells B and C in the current row
    // Left-justify the content in the merged cell
    row.getCell(1).alignment = { horizontal: 'left', vertical: 'top' };

    row = worksheetComplianceReport.addRow([this.confidential_info_body]);
    rowIndex = row.number; // Get the current row index
    worksheetComplianceReport.mergeCells(rowIndex, 1, rowIndex, 4); // Merge cells B and C in the current row
    row.getCell(1).alignment = { horizontal: 'left', vertical: 'top', wrapText: true };
    // Set row height manually (adjust based on content length)
    row.height = 110; // Adjust the height value as needed
  }

  private convertDateToMMDDYYYY(row, date: Date | string, submission: String): string {
    if (!date) {
      if (submission === 'N/A') {
        return 'N/A'; // Return 'N/A' if submission is 'N/A'
      }

      return ''; // Return empty string if date is null or undefined
    }

    try {
      const parsedDate = typeof date === 'string' ? new Date(date) : date; // Parse if input is a string
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0'); // Get month (0-based, so add 1)
      const day = String(parsedDate.getDate()).padStart(2, '0'); // Get day
      const year = parsedDate.getFullYear(); // Get year
      return `${month}/${day}/${year}`; // Format as mm/dd/yyyy
    } catch (error) {
      console.error('Error in convertDateToMMDDYYYY: ', error);
      console.error('Error in row: ', row);
      return '';
    }
  }

  private calculateDaysBetweenDates(row, startDate: Date | string | null, endDate: Date | string | null, submission: String): string {
    if (!startDate && submission === 'N/A') {
      return 'N/A'; // Return 'N/A' if submission is 'N/A'
    }

    startDate = this.convertDateToMMDDYYYY(row, startDate, '');
    endDate = this.convertDateToMMDDYYYY(row, endDate, '');

    if (!startDate || !endDate) {
      return ''; // Return empty string if either date is null or empty
    }

    try {
      // Parse the input strings as dates
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Validate the parsed dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return ''; // Return empty string if either date is invalid
      }

      // Calculate the difference in milliseconds
      const differenceInTime = end.getTime() - start.getTime();

      let differenceInDays;
      if (differenceInTime >= 0) {
        differenceInDays = Math.ceil(differenceInTime / (1000 * 60 * 60 * 24));
      } else {
        differenceInDays = Math.floor(differenceInTime / (1000 * 60 * 60 * 24));
      }

      // Return the result, with negative values in brackets
      return differenceInDays < 0 ? `(${Math.abs(differenceInDays)})` : differenceInDays.toString();
    } catch (error) {
      console.error('Error calculating days between dates:', error);
      return ''; // Return empty string in case of an error
    }
  }

  private generateFileName(baseName: string, extension: string): string {
    const now = new Date();
  
    // Format date and time
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
  
    // Combine into a timestamp
    const timestamp = `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
  
    // Return the file name
    return `${baseName}_${timestamp}.${extension}`;
  }
}    